import { useState } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { X, Pencil, Crown, Loader2 } from "lucide-react";

/**
 * Settings dialog — rename the team and (one-way) hand the lead role to
 * another active member. Both actions hit dedicated endpoints we built
 * in Phase 2. Transfer-lead requires a confirm step since it's no-undo.
 */
export default function SettingsDialog({ workspace, members, onClose, onChanged }) {
  const [name, setName] = useState(workspace.name);
  const [renaming, setRenaming] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(false);

  const eligibleMembers = members.filter((m) => m.status === "active" && m.user_id);

  const rename = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be blank."); return; }
    if (trimmed === workspace.name) { toast("Same name — nothing to update."); return; }
    setRenaming(true);
    try {
      await axios.patch(`${API}/workspaces/${workspace.id}`, { name: trimmed });
      toast.success("Team renamed.");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Rename failed."));
    } finally {
      setRenaming(false);
    }
  };

  const transferLead = async () => {
    if (!transferTo) { toast.error("Pick a designer to hand the lead role to."); return; }
    const target = eligibleMembers.find((m) => m.user_id === transferTo);
    if (!target) return;
    if (!window.confirm(
      `Hand the team lead role to ${target.name || target.email}? You'll keep your credits but lose admin access — only they can manage the team afterwards.`,
    )) return;
    setTransferring(true);
    try {
      await axios.post(
        `${API}/workspaces/${workspace.id}/transfer-lead`,
        { new_lead_user_id: transferTo },
      );
      toast.success(`Lead role transferred to ${target.name || target.email}.`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err, "Transfer failed."));
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="team-settings-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-[#0E0E0E] border border-white/10 p-7"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 text-white/55 hover:text-white flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <h2 className="font-display text-2xl text-white">Team settings</h2>

        {/* Rename */}
        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Team name</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              data-testid="team-settings-name-input"
              className="flex-1 bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
            />
            <button
              type="button"
              onClick={rename}
              disabled={renaming || !name.trim() || name.trim() === workspace.name}
              data-testid="team-settings-rename-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-md bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition disabled:opacity-50"
            >
              {renaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
        </div>

        {/* Transfer lead */}
        <div className="mt-7 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-amber-300">
            <Crown className="w-3 h-3" /> Transfer lead role
          </div>
          <p className="text-xs text-white/55 mt-1.5">
            Hand admin access to one of your designers. You&apos;ll become a
            regular member — keep your credits, lose your management
            powers. No-undo.
          </p>
          {eligibleMembers.length === 0 ? (
            <p className="text-[11px] text-white/45 mt-3">
              No eligible designers yet — invitees must accept before they can become lead.
            </p>
          ) : (
            <>
              <select
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                data-testid="team-settings-transfer-target"
                className="mt-3 w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
              >
                <option value="">Select a designer…</option>
                {eligibleMembers.map((m) => (
                  <option key={m.id} value={m.user_id}>{m.name || m.email}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={transferLead}
                disabled={transferring || !transferTo}
                data-testid="team-settings-transfer-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-2.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 transition disabled:opacity-50"
              >
                {transferring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crown className="w-3.5 h-3.5" />}
                {transferring ? "Transferring…" : "Transfer lead role"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
